"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { ArrowRight, BookOpen, Calendar, Clock, RotateCw } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import Reveal from "@/components/common/Reveal";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
import { COMMON, type L6 } from "@/lib/commonCopy";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
const articlesCopy = {
  badge: {
    en: "Technical Resources",
    zh: "技术资源",
    es: "Recursos técnicos",
    de: "Technische Ressourcen",
    fr: "Ressources techniques",
    pt: "Recursos técnicos"
  },
  heading: {
    en: "Related technical articles",
    zh: "相关技术文章",
    es: "Artículos técnicos relacionados",
    de: "Verwandte technische Fachartikel",
    fr: "Articles techniques associés",
    pt: "Artigos técnicos relacionados"
  },
  description: {
    en: "Review operational guides, screen mesh configurations, and extrusion filtration best practices.",
    zh: "查看操作指南、滤网结构配置及挤出过滤应用实践。",
    es: "Consulte guías operativas, configuraciones de mallas de filtración y mejores prácticas en extrusión.",
    de: "Lesen Sie Betriebsleitfäden, Siebgewebekonfigurationen und bewährte Verfahren der Extrusionsfiltration.",
    fr: "Consultez les guides opérationnels, les configurations de toiles de filtration et les meilleures pratiques d'extrusion.",
    pt: "Consulte guias operacionais, configurações de telas de filtragem e práticas recomendadas de extrusão."
  },
  viewAll: {
    en: "View all publications",
    zh: "查看全部出版物",
    es: "Ver todas las publicaciones",
    de: "Alle Publikationen anzeigen",
    fr: "Voir toutes les publications",
    pt: "Ver todas as publicações"
  },
  loadError: {
    en: "Unable to load technical articles at this time.",
    zh: "暂无法加载技术文章。",
    es: "No se pueden cargar los artículos técnicos en este momento.",
    de: "Technische Artikel können derzeit nicht geladen werden.",
    fr: "Impossible de charger les articles techniques pour le moment.",
    pt: "Não foi possível carregar os artigos técnicos no momento."
  },
  retry: {
    en: "Retry loading",
    zh: "重新加载",
    es: "Reintentar carga",
    de: "Erneut laden",
    fr: "Réessayer le chargement",
    pt: "Tentar novamente"
  },
  noArticlesTitle: {
    en: "No articles published",
    zh: "暂无已发布文章",
    es: "No hay artículos publicados",
    de: "Keine Artikel veröffentlicht",
    fr: "Aucun article publié",
    pt: "Nenhum artigo publicado"
  },
  noArticlesDesc: {
    en: "Check back soon for updated extrusion and filtration technical notes.",
    zh: "请稍后查看更新的挤出与过滤技术说明。",
    es: "Vuelva pronto para ver notas técnicas actualizadas sobre extrusión y filtración.",
    de: "Besuchen Sie uns bald wieder für aktualisierte technische Hinweise zur Extrusion und Filtration.",
    fr: "Revenez bientôt pour des notes techniques actualisées sur l'extrusion et la filtration.",
    pt: "Volte em breve para notas técnicas atualizadas sobre extrusão e filtração."
  },
  defaultCategory: {
    en: "Technical Note",
    zh: "技术说明",
    es: "Nota técnica",
    de: "Technischer Hinweis",
    fr: "Note technique",
    pt: "Nota técnica"
  },
  defaultExcerpt: {
    en: "Technical breakdown covering stainless steel mesh specifications, alloy selection, and operating tolerances.",
    zh: "涵盖不锈钢丝网规格、合金选型及工作公差的技术分析。",
    es: "Análisis técnico sobre especificaciones de mallas de acero inoxidable, selección de aleaciones y tolerancias.",
    de: "Technische Analyse zu Edelstahlsiebgewebespezifikationen, Legierungswahl und Betriebstoleranzen.",
    fr: "Analyse technique couvrant les spécifications des toiles en acier inoxydable, le choix des alliages et les tolérances.",
    pt: "Análise técnica abrangendo especificações de telas de aço inoxidável, seleção de ligas e tolerâncias operacionais."
  },
  technicalReference: {
    en: "Technical Reference",
    zh: "技术参考",
    es: "Referencia técnica",
    de: "Technische Referenz",
    fr: "Référence technique",
    pt: "Referência técnica"
  },
  coverAltFallback: {
    en: "Technical article cover",
    zh: "技术文章配图",
    es: "Portada del artículo técnico",
    de: "Titelbild des Fachartikels",
    fr: "Image de l'article technique",
    pt: "Capa do artigo técnico"
  }
};
const dateLocales: Record<Lang, string> = {
  en: "en-US",
  zh: "zh-CN",
  es: "es-ES",
  de: "de-DE",
  fr: "fr-FR",
  pt: "pt-PT"
};
function ArticlesGridConsumer() {
  const {
    lang
  } = useLanguage();
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({
        length: 6
      }).map((_, i) => <div key={i} data-index={i} className="flex flex-col rounded-lg border border-border bg-card p-6">
            <Skeleton className="mb-4 h-48 w-full rounded-md" />
            <div className="mb-3 flex items-center gap-2">
              <Skeleton className="h-5 w-20" />
              <Skeleton className="h-4 w-16" />
            </div>
            <Skeleton className="mb-2 h-6 w-3/4" />
            <Skeleton className="mb-4 h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
            <div className="mt-auto pt-6">
              <Skeleton className="h-9 w-28" />
            </div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center rounded-lg border border-border bg-card p-10 text-center">
        <p className="mb-4 text-sm text-muted-foreground">
          {pick(lang, articlesCopy.loadError)}
        </p>
        <Button variant="outline" size="sm" onClick={() => refetch()} className="gap-2">
          <RotateCw className="size-4" aria-hidden="true" />
          {pick(lang, articlesCopy.retry)}
        </Button>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="rounded-lg border border-border bg-card p-12 text-center">
        <BookOpen className="mx-auto mb-3 size-8 text-muted-foreground" />
        <h3 className="text-base font-semibold text-foreground">
          {pick(lang, articlesCopy.noArticlesTitle)}
        </h3>
        <p className="mt-1 text-sm text-muted-foreground">
          {pick(lang, articlesCopy.noArticlesDesc)}
        </p>
      </div>;
  }
  return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
      {posts.map((post, i) => {
      const categoryName = post.categories?.[0]?.name || pick(lang, articlesCopy.defaultCategory);
      const formattedDate = post.date ? new Date(post.date).toLocaleDateString(dateLocales[lang] ?? "en-US", {
        year: "numeric",
        month: "short",
        day: "numeric"
      }) : null;
      return <Reveal key={post.id || i} delay={Math.min(i * 60, 300)}>
            <article data-index={i} className="flex h-full flex-col">
              <Card className="flex h-full flex-col justify-between border-border bg-card rounded-lg overflow-hidden transition-colors duration-150 hover:border-primary/40">
                <div>
                  {post.featuredImage && <div className="relative mf-card-media mf-ratio-card w-full overflow-hidden">
                      <img src={post.featuredImage} alt={post.title || pick(lang, articlesCopy.coverAltFallback)} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                    </div>}

                  <CardHeader className="space-y-3 pt-6">
                    <div className="flex flex-wrap items-center justify-between gap-2 mf-caption text-muted-foreground">
                      <Badge variant="outline" className="bg-muted text-foreground font-mono mf-caption">
                        {categoryName}
                      </Badge>
                      {formattedDate && <span className="inline-flex items-center gap-1 font-mono">
                          <SafeHydrate><Calendar className="size-3.5" aria-hidden="true" /></SafeHydrate>
                          <time dateTime={post.date}>{formattedDate}</time>
                        </span>}
                    </div>

                    <CardTitle className="font-default text-lg font-semibold tracking-tight text-card-foreground">
                      {post.link ? <Link to={post.link} className="transition-colors hover:text-primary">
                          {post.title}
                        </Link> : post.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent>
                    {post.excerpt ? <p className="line-clamp-3 text-sm leading-relaxed text-muted-foreground">
                        {post.excerpt.replace(/<[^>]*>?/gm, "")}
                      </p> : <p className="line-clamp-3 text-sm leading-relaxed text-muted-foreground">
                        {pick(lang, articlesCopy.defaultExcerpt)}
                      </p>}
                  </CardContent>
                </div>

                <CardFooter className="pt-2">
                  {post.link ? <Button asChild variant="outline" size="sm" className="w-full justify-between group">
                      <Link to={post.link}>
                        <span>{pick(lang, COMMON.readMore)}</span>
                        <ArrowRight className="size-3.5" aria-hidden="true" />
                      </Link>
                    </Button> : <div className="flex items-center gap-1 mf-caption text-muted-foreground font-mono">
                      <Clock className="size-3.5" aria-hidden="true" />
                      <span>{pick(lang, articlesCopy.technicalReference)}</span>
                    </div>}
                </CardFooter>
              </Card>
            </article>
          </Reveal>;
    })}
    </div>;
}
export default function Articles() {
  const {
    lang
  } = useLanguage();
  return <section id="relatedarticles" data-nav="light" className="border-t border-border bg-background mf-section">
      <div className="mf-container">
        <Reveal>
          <div className="mb-10 flex flex-col items-start justify-between gap-4 border-b border-border pb-6 md:flex-row md:items-end">
            <div className="max-w-2xl">
              <Badge variant="outline" className="mb-3 font-mono mf-caption uppercase">
                {pick(lang, articlesCopy.badge)}
              </Badge>
              <h2 className="font-default mf-h2 text-foreground">
                {pick(lang, articlesCopy.heading)}
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground sm:text-base">
                {pick(lang, articlesCopy.description)}
              </p>
            </div>

            <Button asChild variant="ghost" size="sm" className="gap-2">
              <Link to="/TechnicalResourcesPage">
                <span>{pick(lang, articlesCopy.viewAll)}</span>
                <ArrowRight className="size-4" aria-hidden="true" />
              </Link>
            </Button>
          </div>
        </Reveal>

        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 6,
        order: "desc",
        orderby: "date",
        status: ["publish"]
      }} className="w-full">
          <ArticlesGridConsumer />
        </PostsProvider>
      </div>
    </section>;
}